import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  TableSortLabel,
  keyframes,
  styled,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { DataTableProps, SortConfig, TableCellParams } from './types';
import { getValueByPath, paginateData, sortData } from './utils';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components with animations
const AnimatedPaper = styled(Paper)<{ disableAnimation?: boolean }>(
  ({ disableAnimation = false }) => ({
    width: '100%',
    overflow: 'hidden',
    borderRadius: 8,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    animation: disableAnimation ? 'none' : `${fadeInUp} 0.6s ease-out`,
  })
);

const AnimatedTableContainer = styled(TableContainer)<{ disableAnimation?: boolean }>(
  ({ disableAnimation = false }) => ({
    animation: disableAnimation ? 'none' : `${fadeIn} 0.8s ease-out 0.2s both`,
  })
);

const AnimatedTableHead = styled(TableHead)<{ disableAnimation?: boolean }>(
  ({ disableAnimation = false }) => ({
    animation: disableAnimation ? 'none' : `${slideInDown} 0.7s ease-out 0.1s both`,
  })
);

const AnimatedTableRow = styled(TableRow)<{
  animationDelay?: number;
  disableAnimation?: boolean;
  isDataRow?: boolean;
}>(({ animationDelay = 0, disableAnimation = false, isDataRow = false }) => ({
  animation:
    disableAnimation || !isDataRow ? 'none' : `${fadeIn} 0.6s ease-out ${animationDelay}ms both`,
}));

/**
 * A reusable data table component with sorting, pagination, and action menu
 */
function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  actions = [],
  keyField,
  title,
  showTitle = true,
  loading = false,
  initialSortField,
  initialSortDirection = 'asc',
  pagination = true,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  emptyState,
  hover = true,
  striped = true,
  toolbarContent,
  sortable = true,
  stickyHeader = true,
  maxHeight = 600,
  sx,
  containerSx,
  onRowClick,
  onCellClick,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  showActions = true,
  className,
  dense = false,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  totalCount,
  animationDelay = 0,
  disableAnimation = false,
}: DataTableProps<T> & { animationDelay?: number; disableAnimation?: boolean }) {
  // State for sorting
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(
    initialSortField ? { field: initialSortField as string, direction: initialSortDirection } : null
  );

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  // State for action menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  // Use controlled values if provided
  const currentPage = controlledPage !== undefined ? controlledPage : page;
  const currentRowsPerPage =
    controlledRowsPerPage !== undefined ? controlledRowsPerPage : rowsPerPage;

  // Reset page when data changes
  useEffect(() => {
    if (controlledPage === undefined) {
      setPage(0);
    }
  }, [data, controlledPage]);

  // Handle sort request
  const handleSort = (field: string) => {
    let direction: 'asc' | 'desc' = 'asc';

    if (sortConfig && sortConfig.field === field) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }

    setSortConfig({ field, direction });

    if (onSortChange) {
      onSortChange(field as keyof T, direction);
    }
  };

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);

    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (onRowsPerPageChange) {
      onRowsPerPageChange(newRowsPerPage);
    }
  };

  // Handle action menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: T, rowIndex: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
    setSelectedRowIndex(rowIndex);
  };

  // Handle action menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
    setSelectedRowIndex(null);
  };

  // Handle action click
  const handleActionClick = (action: (params: { row: T; rowIndex: number }) => void) => {
    if (selectedRow !== null && selectedRowIndex !== null) {
      action({ row: selectedRow, rowIndex: selectedRowIndex });
      handleMenuClose();
    }
  };

  // Process data: sort and paginate
  const processedData = useMemo(() => {
    // Client-side sorting (if not controlled by parent)
    const sortedData = onSortChange && sortConfig ? data : sortData(data, sortConfig, columns);

    // Client-side pagination (if not controlled by parent)
    if (onPageChange && onRowsPerPageChange) {
      return sortedData;
    }

    return paginateData(sortedData, currentPage, currentRowsPerPage);
  }, [
    data,
    sortConfig,
    columns,
    currentPage,
    currentRowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
  ]);

  // Visible actions for a row
  const getVisibleActions = (row: T, rowIndex: number) => {
    return actions.filter(action => !action.show || action.show({ row, rowIndex }));
  };

  // Render cell content
  const renderCellContent = (params: TableCellParams<T>) => {
    const { column, row, value } = params;

    if (column.renderCell) {
      return column.renderCell(params);
    }

    if (column.valueFormatter) {
      return column.valueFormatter(value, row);
    }

    if (value === null || value === undefined) {
      return '';
    }

    return String(value);
  };

  // Empty state
  const renderEmptyState = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (emptyState) {
      return emptyState;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No data to display
        </Typography>
      </Box>
    );
  };

  return (
    <AnimatedPaper disableAnimation={disableAnimation} sx={containerSx} className={className}>
      {/* Table Title and Toolbar */}
      {(showTitle && title) || toolbarContent ? (
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
            animation: disableAnimation
              ? 'none'
              : `${fadeIn} 0.7s ease-out ${animationDelay + 100}ms both`,
          }}
        >
          {showTitle && title && (
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 600,
              }}
            >
              {title}
            </Typography>
          )}
          {toolbarContent}
        </Box>
      ) : null}

      {/* Table */}
      <AnimatedTableContainer 
        sx={{ 
          maxHeight,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: 4,
          },
        }} 
        disableAnimation={disableAnimation}
      >
        <Table 
          stickyHeader={stickyHeader} 
          size={dense ? 'small' : 'medium'} 
          sx={{
            ...sx,
            minWidth: { xs: 600, sm: 'auto' }, // Ensure minimum width for mobile scrolling
          }}
        >
          <AnimatedTableHead disableAnimation={disableAnimation}>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  padding: { 
                    xs: dense ? '6px 8px' : '8px 12px', 
                    sm: dense ? '8px 16px' : '16px' 
                  },
                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                },
              }}
            >
              {columns.map(
                column =>
                  !column.hide && (
                    <TableCell
                      key={column.field as string}
                      align={column.align || 'left'}
                      style={{
                        minWidth: column.minWidth,
                        width: column.width,
                        flex: column.flex,
                      }}
                      className={column.headerClassName}
                    >
                      {column.renderHeader ? (
                        column.renderHeader(column)
                      ) : sortable && column.sortable !== false ? (
                        <TableSortLabel
                          active={sortConfig?.field === column.field}
                          direction={
                            sortConfig?.field === column.field ? sortConfig.direction : 'asc'
                          }
                          onClick={() => handleSort(column.field as string)}
                          sx={{
                            '& .MuiTableSortLabel-icon': {
                              color: 'primary.main !important',
                            },
                            color: 'primary.main !important',
                          }}
                        >
                          {column.headerName}
                        </TableSortLabel>
                      ) : (
                        column.headerName
                      )}
                    </TableCell>
                  )
              )}

              {/* Action Column */}
              {showActions && actions.length > 0 && <TableCell align="center">Actions</TableCell>}
            </TableRow>
          </AnimatedTableHead>

          <TableBody>
            {processedData.length > 0 ? (
              processedData.map((row, rowIndex) => {
                // Only show row if it has at least one visible action
                const visibleActions = getVisibleActions(row, rowIndex);
                const showRow = !showActions || actions.length === 0 || visibleActions.length > 0;

                if (!showRow) return null;

                return (
                  <AnimatedTableRow
                    key={String(row[keyField])}
                    hover={hover}
                    onClick={() => onRowClick?.({ row, rowIndex })}
                    animationDelay={animationDelay + 300 + rowIndex * 50}
                    disableAnimation={disableAnimation}
                    isDataRow={true}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      borderLeft: '4px solid transparent',
                      '&:hover': {
                        borderLeft: theme => `4px solid ${theme.palette.primary.main}`,
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        transition: 'all 0.2s ease',
                      },
                      // Zebra striping for better readability
                      backgroundColor: striped
                        ? rowIndex % 2 === 0
                          ? 'white'
                          : 'rgba(0, 0, 0, 0.01)'
                        : 'inherit',
                    }}
                  >
                    {columns.map(
                      column =>
                        !column.hide && (
                          <TableCell
                            key={column.field as string}
                            align={column.align || 'left'}
                            sx={{
                              py: { xs: dense ? 0.75 : 1, sm: dense ? 1 : 2 },
                              borderColor: 'rgba(224, 224, 224, 0.5)',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                            className={column.cellClassName}
                            onClick={e => {
                              e.stopPropagation();
                              onCellClick?.({
                                row,
                                value: getValueByPath(row, column.field as string),
                                field: column.field,
                                column,
                                rowIndex,
                              });
                            }}
                          >
                            {renderCellContent({
                              row,
                              value: getValueByPath(row, column.field as string),
                              field: column.field,
                              column,
                              rowIndex,
                            })}
                          </TableCell>
                        )
                    )}

                    {/* Action Column */}
                    {showActions && actions.length > 0 && visibleActions.length > 0 && (
                      <TableCell
                        align="center"
                        sx={{ 
                          py: { xs: dense ? 0.75 : 1, sm: dense ? 1 : 2 }, 
                          borderColor: 'rgba(224, 224, 224, 0.5)',
                          minWidth: { xs: 60, sm: 80 }
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="More Options">
                            <IconButton
                              size="small"
                              aria-label="more options"
                              onClick={e => handleMenuOpen(e, row, rowIndex)}
                              sx={{
                                p: { xs: 0.5, sm: 1 }
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </AnimatedTableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    columns.filter(col => !col.hide).length +
                    (showActions && actions.length > 0 ? 1 : 0)
                  }
                >
                  {renderEmptyState()}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </AnimatedTableContainer>

      {/* Pagination */}
      {pagination && data.length > 0 && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalCount || data.length}
          rowsPerPage={currentRowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            animation: disableAnimation
              ? 'none'
              : `${fadeIn} 0.8s ease-out ${animationDelay + 500}ms both`,
          }}
        />
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: '8px',
            minWidth: '160px',
            overflow: 'hidden',
          },
        }}
      >
        {selectedRow &&
          selectedRowIndex !== null &&
          getVisibleActions(selectedRow, selectedRowIndex).map((action, index) => (
            <MenuItem
              key={index}
              onClick={() => handleActionClick(action.onClick)}
              sx={{
                py: 1.5,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                color: action.danger
                  ? 'error.main'
                  : action.color
                    ? `${action.color}.main`
                    : 'inherit',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {action.icon}
                <Typography variant="body2" sx={{ ml: 1.5 }}>
                  {action.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
      </Menu>
    </AnimatedPaper>
  );
}

export default DataTable;
